/**
 * Sysadmin Catalog → Tools hooks (PR-H).
 *
 * Wraps /admin/platform/catalog/tools and /admin/platform/tenant-tool-assignments.
 * Mirrors the use-sysadmin-prompts pattern (catalog.prompts sibling).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

export type ToolStatus = 'draft' | 'published' | 'deprecated' | 'retired';

export type ToolSourceType =
  | 'builtin'
  | 'mcp'
  | 'openapi'
  | 'http'
  | 'database'
  | 'custom_function';

export type ToolPolicy = 'auto_approve' | 'require_approval' | 'forbidden';

export interface SysadminToolListItem {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  sourceType: string;
  policy: string;
  category: string;
  version: number;
  isActive: boolean;
  status: string;
  publishedAt: string | null;
  unpublishedAt: string | null;
  unpublishReason: string | null;
  mcpServerId: string | null;
  assignedTenantCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SysadminToolDetail extends SysadminToolListItem {
  sourceConfig: unknown;
  inputSchema: unknown;
  outputSchema: unknown;
  sideEffects: string[];
  publishedBy: string | null;
  unpublishedBy: string | null;
}

export interface ToolListResponse {
  items: SysadminToolListItem[];
  nextCursor: string | null;
}

export interface ToolVersion {
  id: string;
  version: number;
  snapshot: unknown;
  changedByAdmin: string | null;
  changedAt: string;
}

export interface ToolAssignment {
  id: string;
  organizationId: string;
  organizationName: string;
  systemToolId: string;
  toolSlug: string;
  toolDisplayName: string;
  isEnabled: boolean;
  notes: string | null;
  assignedAt: string;
  lastModifiedAt: string;
}

const KEYS = {
  list: (q: object) => ['sysadmin-tools', 'list', q] as const,
  detail: (id: string) => ['sysadmin-tools', 'detail', id] as const,
  versions: (id: string) => ['sysadmin-tools', 'versions', id] as const,
  assignments: (id: string) => ['sysadmin-tools', 'assignments', id] as const,
};

export interface UseSysadminToolsParams {
  status?: ToolStatus;
  sourceType?: ToolSourceType;
  category?: string;
  search?: string;
  mcpServerId?: string;
  limit?: number;
  cursor?: string | null;
}

export function useSysadminTools(params: UseSysadminToolsParams = {}) {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.sourceType) qs.set('sourceType', params.sourceType);
  if (params.category) qs.set('category', params.category);
  if (params.search) qs.set('search', params.search);
  if (params.mcpServerId) qs.set('mcpServerId', params.mcpServerId);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.cursor) qs.set('cursor', params.cursor);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<ToolListResponse, Error>({
    queryKey: KEYS.list(params),
    queryFn: () =>
      apiClient.get<ToolListResponse>(`/admin/platform/catalog/tools${suffix}`),
  });
}

export function useSysadminTool(id: string | undefined) {
  return useQuery<{ data: SysadminToolDetail }, Error>({
    queryKey: KEYS.detail(id ?? ''),
    queryFn: () =>
      apiClient.get<{ data: SysadminToolDetail }>(
        `/admin/platform/catalog/tools/${id}`,
      ),
    enabled: Boolean(id),
  });
}

export function useSysadminToolVersions(id: string | undefined) {
  return useQuery<{ items: ToolVersion[] }, Error>({
    queryKey: KEYS.versions(id ?? ''),
    queryFn: () =>
      apiClient.get<{ items: ToolVersion[] }>(
        `/admin/platform/catalog/tools/${id}/versions`,
      ),
    enabled: Boolean(id),
  });
}

export function useSysadminToolAssignments(id: string | undefined) {
  return useQuery<{ items: ToolAssignment[] }, Error>({
    queryKey: KEYS.assignments(id ?? ''),
    queryFn: () =>
      apiClient.get<{ items: ToolAssignment[] }>(
        `/admin/platform/catalog/tools/${id}/assignments`,
      ),
    enabled: Boolean(id),
  });
}

export interface CreateToolInput {
  slug: string;
  displayName: string;
  description: string;
  sourceType: ToolSourceType;
  sourceConfig?: Record<string, unknown>;
  inputSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  policy?: ToolPolicy;
  sideEffects?: string[];
  category?: string;
  mcpServerId?: string | null;
}

export function useCreateSysadminTool() {
  const qc = useQueryClient();
  return useMutation<{ data: SysadminToolDetail }, Error, CreateToolInput>({
    mutationFn: (input) =>
      apiClient.post<{ data: SysadminToolDetail }>(
        '/admin/platform/catalog/tools',
        input,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['sysadmin-tools', 'list'] }),
  });
}

export interface UpdateToolInput {
  id: string;
  displayName?: string;
  description?: string;
  sourceType?: ToolSourceType;
  sourceConfig?: Record<string, unknown>;
  inputSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  policy?: ToolPolicy;
  sideEffects?: string[];
  category?: string;
  mcpServerId?: string | null;
}

export function useUpdateSysadminTool() {
  const qc = useQueryClient();
  return useMutation<{ data: SysadminToolDetail }, Error, UpdateToolInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch<{ data: SysadminToolDetail }>(
        `/admin/platform/catalog/tools/${id}`,
        body,
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-tools', 'list'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
      qc.invalidateQueries({ queryKey: KEYS.versions(vars.id) });
    },
  });
}

export function useDeleteSysadminTool() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete(`/admin/platform/catalog/tools/${id}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-tools', 'list'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

export function usePublishSysadminTool() {
  const qc = useQueryClient();
  return useMutation<{ data: SysadminToolDetail }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post<{ data: SysadminToolDetail }>(
        `/admin/platform/catalog/tools/${id}/publish`,
        {},
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-tools', 'list'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

export type UnpublishNextStatus = 'deprecated' | 'retired';

export function useUnpublishSysadminTool() {
  const qc = useQueryClient();
  return useMutation<
    { data: SysadminToolDetail; revokedAssignments: number },
    Error,
    { id: string; reason: string; nextStatus: UnpublishNextStatus }
  >({
    mutationFn: ({ id, reason, nextStatus }) =>
      apiClient.post<{ data: SysadminToolDetail; revokedAssignments: number }>(
        `/admin/platform/catalog/tools/${id}/unpublish`,
        { reason, nextStatus },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-tools', 'list'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
      qc.invalidateQueries({ queryKey: KEYS.assignments(vars.id) });
    },
  });
}

export function useAssignSysadminTool() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; alreadyExists?: true },
    Error,
    { organizationId: string; systemToolId: string; notes?: string }
  >({
    mutationFn: (body) =>
      apiClient.post<{ id: string; alreadyExists?: true }>(
        '/admin/platform/tenant-tool-assignments',
        body,
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({
        queryKey: KEYS.assignments(vars.systemToolId),
      });
      qc.invalidateQueries({ queryKey: ['sysadmin-tools', 'list'] });
      qc.invalidateQueries({
        queryKey: KEYS.detail(vars.systemToolId),
      });
    },
  });
}

export function useRevokeSysadminToolAssignment() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { id: string; systemToolId: string }
  >({
    mutationFn: ({ id }) =>
      apiClient.delete(`/admin/platform/tenant-tool-assignments/${id}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({
        queryKey: KEYS.assignments(vars.systemToolId),
      });
      qc.invalidateQueries({ queryKey: ['sysadmin-tools', 'list'] });
      qc.invalidateQueries({
        queryKey: KEYS.detail(vars.systemToolId),
      });
    },
  });
}

// ─── Tenants list (for the Assign drawer) ─────────────────────────────────

export interface TenantOption {
  id: string;
  name: string;
  slug: string;
  status: string;
}

interface TenantListResponse {
  items: TenantOption[];
}

export function useTenantsForAssignment() {
  return useQuery<TenantListResponse, Error>({
    queryKey: ['sysadmin-tools', 'tenants-for-assignment'],
    queryFn: () =>
      apiClient.get<TenantListResponse>(
        '/admin/platform/tenants?status=active',
      ),
  });
}
